import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import SEO from "../../components/base/SEO";

const benefits = [
  {
    icon: "ri-temp-cold-line",
    title: "Heat & Cool in One Unit",
    desc: "A mini split heat pump both heats and cools your home — eliminating the need for separate furnace and AC systems.",
  },
  {
    icon: "ri-plug-line",
    title: "Up to 40% Energy Savings",
    desc: "Heat pumps move heat rather than generate it, making them 2–3x more efficient than traditional heating systems.",
  },
  {
    icon: "ri-layout-grid-line",
    title: "No Ductwork Needed",
    desc: "Perfect for additions, garages, sunrooms, and older homes — mini splits require only a small hole in the wall.",
  },
  {
    icon: "ri-remote-control-line",
    title: "Zone-by-Zone Control",
    desc: "Each indoor unit has its own thermostat, so every room stays at exactly the temperature you prefer.",
  },
  {
    icon: "ri-leaf-line",
    title: "Eco-Friendly Operation",
    desc: "Using R-410A or R-454B refrigerant and high SEER2 ratings, mini splits dramatically reduce your carbon footprint.",
  },
  {
    icon: "ri-volume-mute-line",
    title: "Whisper-Quiet Performance",
    desc: "Indoor units operate as low as 19 dB — quieter than a library — keeping your home peaceful and comfortable.",
  },
];

const systemTypes = [
  {
    name: "Single-Zone Mini Split",
    tagline: "Perfect for one room or addition",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/bb033eda1be3cdd2d559b35e52d9cad9.png",
    desc: "Ideal for heating and cooling a single space — a bedroom, home office, sunroom, or garage. One outdoor condenser connects to one indoor air handler for simple, efficient comfort.",
    features: ["1 indoor + 1 outdoor unit", "9,000–36,000 BTU capacity", "Best for spaces up to 1,500 sq ft", "Quick 4–6 hour installation"],
    highlight: false,
  },
  {
    name: "Multi-Zone Mini Split",
    tagline: "Whole-home comfort, one outdoor unit",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/7b008d860d3b675911e54eb54f676fbf.png",
    desc: "One outdoor unit powers up to 5 indoor heads — each independently controlled. The smart choice for whole-home comfort without the cost and mess of duct installation.",
    features: ["Up to 5 indoor zones", "18,000–60,000 BTU output", "Independent room control", "Single outdoor footprint"],
    highlight: true,
  },
  {
    name: "Commercial Heating & Cooling",
    tagline: "All-in-one rooftop package system",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/45a2b4edeafb4163d2dcafda1ee53923.jpeg",
    desc: "Self-contained packaged units installed on flat commercial roofs — combining heating, cooling, and ventilation in one cabinet. Ideal for retail spaces, warehouses, offices, and light industrial buildings with ducted distribution. We install &amp; maintain these behemoths!",
    features: ["3–50 ton capacity range", "Gas/electric or heat pump options", "High-static duct capability", "Economizer & fresh air intake"],
    highlight: false,
  },
  {
    name: "Ducted Air Handler",
    tagline: "Mini split performance, ducted delivery",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/52be37224099cc4fd4c21846996ddf66.jpeg",
    desc: "Combines the efficiency of a heat pump with your existing ductwork. A concealed air handler installs in an attic or closet and connects directly to your duct system.",
    features: ["Uses existing ductwork", "Hidden installation", "Central system efficiency", "Best for full-home retrofits"],
    highlight: false,
  },
];

const brands = [
  { name: "Mitsubishi Electric", note: "Diamond Dealer — Top-rated for reliability & quiet operation" },
  { name: "Daikin", note: "Gold Partner — Industry-leading inverter technology" },
  { name: "LG", note: "Certified Installer — Art Cool & Mega Multi series" },
  { name: "Carrier", note: "Factory Authorized — Infinity & Performance series" },
  { name: "Bosch", note: "Certified — IDS Premium & Climate 5000 series" },
];

const faqs = [
  {
    q: "How does a mini split heat pump work in cold weather?",
    a: "Modern cold-climate heat pumps — like the Mitsubishi H2i series — are rated to operate down to -13°F (-25°C). They extract ambient heat from outdoor air (even cold air contains heat energy) and transfer it inside, keeping your home warm and efficient all winter long in Connecticut.",
  },
  {
    q: "Can a mini split replace my existing furnace?",
    a: "Yes! Many Connecticut homeowners use multi-zone mini splits as their primary heating and cooling system. For very cold climates, we may recommend a hybrid approach — pairing a heat pump with a backup heating source for the coldest nights.",
  },
  {
    q: "How much does a mini split system cost to install?",
    a: "Single-zone systems typically start around $2,500–$4,500 installed. Multi-zone systems range from $5,000–$12,000+ depending on the number of zones, brand, and complexity of installation. We provide free in-home estimates with no obligation.",
  },
  {
    q: "Are there rebates or tax credits for heat pumps?",
    a: "Yes — federal tax credits cover up to 30% of the installation cost (up to $2,000) under the Inflation Reduction Act. Connecticut also offers utility rebates through Eversource and UI. We help you identify every rebate you qualify for.",
  },
  {
    q: "How long does a mini split installation take?",
    a: "A single-zone installation typically takes 4–6 hours. A multi-zone system with 3–4 heads usually takes one full day. Our technicians handle everything — mounting, line sets, electrical, refrigerant charge, and startup.",
  },
  {
    q: "Do I need to service my mini split regularly?",
    a: "Yes — annual maintenance keeps efficiency high and warranties valid. Our service contract plans include mini split tune-ups, filter cleaning, coil inspection, and refrigerant checks at discounted rates.",
  },
];

const galleryImages = [
  {
    src: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/8523e024908e842842e49c4cd87492b1.jpeg",
    caption: "Outdoor condenser installation on Connecticut home",
  },
  {
    src: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/f024054ff94950117ef1c4cde0563f88.jpeg",
    caption: "Bedroom zone — whisper-quiet wall unit",
  },
  {
    src: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/ea1d8ca9ae74990d91d8080790ae761e.jpeg",
    caption: "Basement zone — finished space made comfortable",
  },
  {
    src: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/f1fc6b45523c2d134fd58da87c0b084b.jpeg",
    caption: "Factory-trained startup & commissioning",
  },
  {
    src: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/4444693b2bf81a37c27b5cf6654c9bcd.jpeg",
    caption: "Multi-zone condenser — 4 rooms, 1 outdoor unit",
  },
  {
    src: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/d7f54f7a1db9baf1eb23c818b311ca81.jpeg",
    caption: "Sunroom & addition — no ductwork needed",
  },
];

const siteUrl: string = (import.meta.env.VITE_SITE_URL as string) ?? "";

const heatPumpsJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Mini Split Heat Pump Installation Connecticut | Ultimate Heating & Cooling",
    description:
      "Expert mini split heat pump installation in Connecticut. Single-zone, multi-zone, and ceiling cassette systems from Mitsubishi, Daikin, LG & Carrier. Free estimates — call (959)241-8524.",
    url: `${siteUrl}/heat-pumps`,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "Heat Pumps", item: `${siteUrl}/heat-pumps` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

export default function HeatPumpsPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Mini Split Heat Pump Installation Connecticut | Ultimate Heating & Cooling"
        description="Expert mini split heat pump installation in Connecticut. Single-zone, multi-zone, and ceiling cassette systems from Mitsubishi, Daikin, LG & Carrier. Free estimates available."
        keywords="mini split heat pump Connecticut, ductless AC installation CT, heat pump installation Somers CT, Mitsubishi mini split installer"
        canonical="/heat-pumps"
        jsonLd={heatPumpsJsonLd}
      />
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative w-full h-[520px] md:h-[600px] overflow-hidden">
        <img
          src="https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/30531f4e68c82738b7580c241dd8cff7.jpeg"
          alt="Mini split heat pump installation Connecticut"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B2447]/92 via-[#0B2447]/70 to-[#0B2447]/20"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-6xl mx-auto px-6 w-full">
            <span className="inline-block bg-[#C8102E] text-white text-[10px] font-bold tracking-[0.2em] px-3 py-1.5 rounded-full mb-5">
              HEAT PUMPS & MINI SPLITS
            </span>
            <h1
              className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5 max-w-2xl"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Connecticut&apos;s Mini Split<br />Heat Pump Experts
            </h1>
            <p className="text-white/80 text-base max-w-xl leading-relaxed mb-8">
              Heat and cool any space — no ductwork required. Ultimate Heating &amp; Cooling installs, services, and maintains ductless mini split systems from the world&apos;s top brands.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="bg-[#C8102E] text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap w-fit"
              >
                GET A FREE ESTIMATE
              </Link>
              <a
                href="tel:9592418524"
                className="border border-white/40 text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap w-fit flex items-center gap-2"
              >
                <i className="ri-phone-line"></i>
                (959) 241-8524
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT IS A MINI SPLIT ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">WHAT IS A MINI SPLIT?</span>
              <h2
                className="text-3xl font-extrabold text-[#0B2447] mt-3 mb-5 leading-tight"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                One System. Year-Round<br />Heating & Cooling.
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                A <strong>mini split heat pump</strong> is a ductless heating and cooling system consisting of two main components: a compact outdoor compressor/condenser and one or more indoor air handlers. The two units are connected by a small conduit carrying refrigerant lines, power, and a condensate drain — requiring only a 3-inch hole in your wall.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Unlike traditional systems, <strong>mini splits deliver conditioned air directly into a room</strong> — eliminating the 20–30% energy loss that occurs in ductwork. In heating mode, they extract heat energy from outdoor air and transfer it inside; in cooling mode, they reverse the process — pulling heat from your home and expelling it outdoors.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Modern cold-climate mini splits like the <strong>Mitsubishi Hyper Heat</strong> maintain full heating capacity down to -13°F — making them a proven, year-round solution for Connecticut winters.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 flex items-center justify-center bg-[#C8102E]/10 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-[#C8102E] text-xs"></i>
                  </span>
                  <span className="text-xs text-gray-700 font-medium">No ductwork required</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 flex items-center justify-center bg-[#C8102E]/10 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-[#C8102E] text-xs"></i>
                  </span>
                  <span className="text-xs text-gray-700 font-medium">Works in -13°F temps</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-5 h-5 flex items-center justify-center bg-[#C8102E]/10 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-[#C8102E] text-xs"></i>
                  </span>
                  <span className="text-xs text-gray-700 font-medium">Install in one day</span>
                </div>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden w-full h-[360px] md:h-[420px]">
              <img
                src="https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/eb61104abf57c80ed8cc5a7f370a9b3a.png"
                alt="How a mini split heat pump works"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-4 left-4 bg-white/95 rounded-xl px-4 py-3 max-w-[240px]">
                <p className="text-[#0B2447] text-xs font-bold">Up to 40% More Efficient</p>
                <p className="text-gray-500 text-[11px] mt-0.5 leading-snug">Than traditional gas furnace + AC setups</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS GRID ── */}
      <section className="py-20 bg-[#F0F4FA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">WHY MINI SPLITS</span>
            <h2
              className="text-2xl font-extrabold text-[#0B2447] mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              The Smart Choice for Connecticut Homeowners
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="w-11 h-11 flex items-center justify-center bg-[#C8102E]/10 rounded-xl mb-4">
                  <i className={`${b.icon} text-xl text-[#C8102E]`}></i>
                </div>
                <h3 className="font-bold text-[#0B2447] text-sm mb-2">{b.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SYSTEM TYPES ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">SYSTEM OPTIONS</span>
            <h2
              className="text-3xl font-extrabold text-[#0B2447] mt-2 mb-3"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Find the Right System for Your Home
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto leading-relaxed">
              Every home is different. We&apos;ll help you choose the right mini split configuration for your space, budget, and comfort goals.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {systemTypes.map((sys) => (
              <div
                key={sys.name}
                className={`rounded-2xl overflow-hidden border-2 ${sys.highlight ? "border-[#C8102E]" : "border-gray-100"}`}
              >
                {sys.highlight && (
                  <div className="bg-[#C8102E] text-white text-[10px] font-bold tracking-[0.18em] text-center py-2">
                    MOST POPULAR
                  </div>
                )}
                <div className="w-full h-52 overflow-hidden">
                  <img
                    src={sys.image}
                    alt={sys.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <span className="text-[#C8102E] text-[10px] font-bold tracking-[0.15em] uppercase">{sys.tagline}</span>
                  <h3
                    className="text-lg font-extrabold text-[#0B2447] mt-1 mb-3"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {sys.name}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">{sys.desc}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {sys.features.map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 bg-[#C8102E]/10 rounded-full">
                          <i className="ri-check-line text-[#C8102E] text-[10px]"></i>
                        </span>
                        <span className="text-xs text-gray-600 leading-snug">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PHOTO GALLERY ── */}
      <section className="py-20 bg-[#F0F4FA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">OUR WORK</span>
            <h2
              className="text-2xl font-extrabold text-[#0B2447] mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Mini Split Installations Across Connecticut
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {galleryImages.map((img) => (
              <div key={img.src} className="rounded-xl overflow-hidden group">
                <div className="relative w-full h-56 overflow-hidden">
                  <img
                    src={img.src}
                    alt={img.caption}
                    title={img.caption}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BRANDS ── */}
      <section className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">BRANDS WE INSTALL</span>
            <h2
              className="text-xl font-extrabold text-[#0B2447] mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Authorized Dealer & Certified Installer
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {brands.map((b) => (
              <div key={b.name} className="bg-[#F0F4FA] rounded-xl px-5 py-5 text-center border border-gray-100">
                <div className="w-9 h-9 flex items-center justify-center bg-[#0B2447]/10 rounded-lg mx-auto mb-3">
                  <i className="ri-award-fill text-[#0B2447] text-base"></i>
                </div>
                <p className="text-[#0B2447] font-bold text-sm mb-1">{b.name}</p>
                <p className="text-gray-400 text-[11px] leading-snug">{b.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TAX CREDIT BANNER ── */}
      <section className="py-14 bg-[#0B2447]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="w-16 h-16 flex items-center justify-center bg-[#C8102E]/20 rounded-2xl flex-shrink-0">
              <i className="ri-money-dollar-circle-fill text-[#C8102E] text-3xl"></i>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase mb-2">2026 FEDERAL TAX CREDIT</p>
              <h3
                className="text-2xl font-extrabold text-white mb-2"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Save Up to $2,000 on Your Heat Pump Installation
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                The Inflation Reduction Act provides a 30% federal tax credit (up to $2,000) on qualifying heat pump installations. Connecticut utility rebates from Eversource &amp; UI may add additional savings. We&apos;ll help you navigate every incentive available.
              </p>
            </div>
            <div className="flex-shrink-0">
              <Link
                to="/contact"
                className="bg-[#C8102E] text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap"
              >
                GET FREE ESTIMATE
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative w-full h-[400px] rounded-2xl overflow-hidden">
              <img
                src="https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/ae07cb39ff2a1153f69cf66a6181482a.jpeg"
                alt="Ultimate Heating & Cooling mini split team Connecticut"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute top-4 left-4 bg-white rounded-xl px-4 py-3">
                <p className="text-[#0B2447] text-xs font-bold">Factory-Trained Technicians</p>
                <p className="text-gray-500 text-[11px] mt-0.5">Certified &amp; Licensed in CT</p>
              </div>
            </div>
            <div>
              <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">WHY CHOOSE ULTIMATE</span>
              <h2
                className="text-3xl font-extrabold text-[#0B2447] mt-3 mb-5 leading-tight"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Mini Split Specialists<br />You Can Trust
              </h2>
              <div className="space-y-4">
                {[
                  { icon: "ri-verified-badge-line", title: "Factory-Certified Installation", desc: "Our technicians are factory-trained and certified by Mitsubishi, Daikin, LG, and Carrier, And More, ensuring your warranty stays fully intact." },
                  { icon: "ri-map-pin-line", title: "Connecticut-Based & Local", desc: "We live and work here. We understand CT building codes, rebate programs, and the unique demands of New England winters." },
                  { icon: "ri-file-list-3-line", title: "Detailed Free Estimates", desc: "We provide itemized, written estimates at no charge — with multiple system options and a breakdown of available rebates and tax credits." },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C8102E]/10 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-lg text-[#C8102E]`}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0B2447] text-sm mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-[#F0F4FA]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">FAQ</span>
            <h2
              className="text-2xl font-extrabold text-[#0B2447] mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Heat Pump & Mini Split Questions Answered
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer group"
                >
                  <span className="text-sm font-semibold text-[#0B2447] group-hover:text-[#C8102E] transition-colors pr-4 leading-snug">
                    {faq.q}
                  </span>
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${
                      openFaq === i ? "bg-[#C8102E] text-white rotate-45" : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <i className="ri-add-line text-base"></i>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 border-t border-gray-100">
                    <p className="text-gray-500 text-sm leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=comfortable%20modern%20home%20interior%20living%20room%20warm%20evening%20lighting%20mini%20split%20heat%20pump%20wall%20unit%20installed%20Connecticut%20family%20home%20cozy%20atmosphere%20soft%20lighting%20beautiful%20residential%20interior%20design&width=1400&height=400&seq=hp-cta-01&orientation=landscape"
          alt="Comfortable home with mini split"
          className="w-full h-72 object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#0B2447]/85"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">READY TO GET STARTED?</span>
            <h2
              className="text-3xl font-extrabold text-white mt-3 mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Get Your Free Mini Split Estimate Today
            </h2>
            <p className="text-white/70 text-sm mb-7 max-w-md mx-auto leading-relaxed">
              No pressure, no gimmicks — just an honest assessment of what system fits your home and budget, with all available rebates applied.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="bg-[#C8102E] text-white font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap"
              >
                SCHEDULE FREE ESTIMATE
              </Link>
              <a
                href="tel:9592418524"
                className="border border-white/40 text-white font-bold text-sm px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i>
                CALL (959) 241-8524
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
